import { EventEmitter, Inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, take, tap } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { DeepPartial } from '../interfaces/deep-partial.type';
import { mergeDeep } from '../utils/merge-deep';
import { LayoutService } from './layout.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.states';
import { loadOperationsAction } from 'src/app/api/operations/operations.actions';

export interface Config {
  id: ConfigName;
  rtl?: boolean;
  name: string;
  imgSrc: string;
  language: string;
  layout: 'vertical' | 'horizontal';
  boxed: boolean;
  sidenav: {
    state: 'expanded' | 'collapsed';
  };
  toolbar: {
    fixed: boolean;
  };
  navbar: {
    position: 'below-toolbar' | 'in-toolbar';
  };
  footer: {
    visible: boolean;
    fixed: boolean;
  };
}

export enum ConfigName {
  apollo = 'vex-layout-apollo',
  zeus = 'vex-layout-zeus',
  hermes = 'vex-layout-hermes',
  poseidon = 'vex-layout-poseidon',
  ares = 'vex-layout-ares',
  ikaros = 'vex-layout-ikaros',
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {

  public languageChanged: EventEmitter<string> = new EventEmitter<string>();

  defaultConfig = ConfigName.apollo;

  /*
    For storing opened task detail to survive page reload
   */
  get taskDetailID(): number {
    let tID = sessionStorage.getItem("taskDetailID");
    return !!Number(tID) ? Number(tID) : null;
  }

  set taskDetailID(value: any) {
    sessionStorage.setItem("taskDetailID", value);
  }

  get detailAgenda(): string {
    let detail = sessionStorage.getItem("detailAgenda");
    return detail;
  }

  set detailAgenda(value: string) {
    sessionStorage.setItem("detailAgenda", value);
  }


  configs: Config[] = [
    {
      id: ConfigName.apollo,
      name: 'Apollo',
      language: localStorage.getItem('lang') || 'cs',
      imgSrc: '',//'https://vex-landing.visurel.com/assets/img/layouts/apollo.png',
      layout: 'horizontal',
      boxed: false,
      sidenav: {
        state: 'expanded'
      },
      toolbar: {
        fixed: true
      },
      navbar: {
        position: 'below-toolbar'
      },
      footer: {
        visible: true,
        fixed: true
      }
    },
    {
      id: ConfigName.hermes,
      name: 'Hermes',
      imgSrc: '', //'https://vex-landing.visurel.com/assets/img/layouts/hermes.png',
      layout: 'vertical',
      language: localStorage.getItem('lang') || 'cs',
      boxed: true,
      sidenav: {
        state: 'expanded'
      },
      toolbar: {
        fixed: false
      },
      navbar: {
        position: 'below-toolbar'
      },
      footer: {
        visible: true,
        fixed: true
      }
    },
    {
      id: ConfigName.ares,
      name: 'Ares',
      imgSrc: '', // 'https://vex-landing.visurel.com/assets/img/layouts/ares.png',
      layout: 'horizontal',
      language: localStorage.getItem('lang') || 'cs',
      boxed: false,
      sidenav: {
        state: 'expanded'
      },
      toolbar: {
        fixed: false
      },
      navbar: {
        position: 'in-toolbar'
      },
      footer: {
        visible: true,
        fixed: true
      }
    },
    {
      id: ConfigName.zeus,
      name: 'Zeus',
      imgSrc: '', // 'https://vex-landing.visurel.com/assets/img/layouts/zeus.png',
      layout: 'horizontal',
      language: localStorage.getItem('lang') || 'cs',
      boxed: false,
      sidenav: {
        state: 'collapsed'
      },
      toolbar: {
        fixed: true
      },
      navbar: {
        position: 'below-toolbar'
      },
      footer: {
        visible: true,
        fixed: true
      }
    },
    {
      id: ConfigName.ikaros,
      name: 'Ikaros',
      imgSrc: '', // 'https://vex-landing.visurel.com/assets/img/layouts/ikaros.png',
      layout: 'vertical',
      language: localStorage.getItem('lang') || 'cs',
      boxed: true,
      sidenav: {
        state: 'expanded'
      },
      toolbar: {
        fixed: false
      },
      navbar: {
        position: 'in-toolbar'
      },
      footer: {
        visible: true,
        fixed: true
      }
    },
  ];

  private _configSubject = new BehaviorSubject(this.configs.find(c => c.id === this.defaultConfig));
  config$ = this._configSubject.asObservable();

  constructor(@Inject(DOCUMENT) private document: Document,
              private translateService: TranslateService,
              private store: Store<AppState>,
              private layoutService: LayoutService) {
    this.config$.subscribe(config => this._updateConfig(config));
  }

  setConfig(config: ConfigName) {
    const settings = this.configs.find(c => c.id === config);

    if (settings) {
      this._configSubject.next(settings);
    }
  }

  async setLanguage(lang: string) {
    if (!lang || lang === 'null') {
      lang = 'cs'; // fallback;
    }
    localStorage.setItem('lang', lang);

    if (this._configSubject.value.language === lang) {
      return;
    }
    
    this.updateConfig({ language: lang });
    this.translateService.reloadLang(lang);
    this.translateService.use(lang);
    this.store.dispatch(loadOperationsAction());

    if (!!this._configSubject.value.language) {
      this.languageChanged.emit(this._configSubject.value.language);
    }
  }

  public getLanguage() {
    return localStorage.getItem('lang') || this._configSubject.value.language;
  }

  updateConfig(config: DeepPartial<Config>) {
    this._configSubject.next(mergeDeep({ ...this._configSubject.getValue() }, config));
  }

  private _updateConfig(config: Config) {
    const body = this.document.body;

    this.configs.forEach(c => {
      if (body.classList.contains(c.id)) {
        body.classList.remove(c.id);
      }
    });

    body.classList.add(config.id);

    config.sidenav.state === 'expanded' ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();

    // Workaround so charts and other externals know they have to resize on Layout switch
    if (window) {
      window.dispatchEvent(new Event('resize'));

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 200);
    }
  }
}
